import React from 'react';
import { ArrowUpDown } from 'lucide-react';
import { inr } from '../poPayment';

const COLS = [
  { key: 'subtotal', label: 'Selling' },
  { key: 'cost', label: 'Cost' },
  { key: 'margin', label: 'Margin' },
  { key: 'margin_pct', label: 'Margin %' },
];

export default function POProfitTable({ rows }) {
  const [sort, setSort] = React.useState({ key: 'margin', dir: 'desc' });
  const costed = React.useMemo(() => {
    const list = (rows || []).filter((r) => r.margin != null);
    const m = sort.dir === 'asc' ? 1 : -1;
    return list.sort((a, b) => ((a[sort.key] ?? 0) - (b[sort.key] ?? 0)) * m);
  }, [rows, sort]);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  const missing = (rows || []).length - costed.length;

  return (
    <div className="soft-card p-4 sm:p-5" data-testid="po-profit-table">
      <h3 className="font-semibold text-[#1a1a2e]">Profit per PO</h3>
      <p className="text-xs text-slate-500 mb-3">Selling price before GST minus internal cost{missing > 0 ? ` · ${missing} PO${missing > 1 ? 's' : ''} have no cost entered` : ''}</p>
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left font-semibold py-2 pr-2">PO</th>
              {COLS.map((c) => (
                <th key={c.key} className="text-right font-semibold py-2 px-2">
                  <button type="button" onClick={() => toggle(c.key)} className={`inline-flex items-center gap-1 hover:text-fuchsia-700 ${sort.key === c.key ? 'text-fuchsia-700' : ''}`} data-testid={`po-profit-sort-${c.key}`}>
                    {c.label}<ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {costed.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">No POs with cost data</td></tr>}
            {costed.map((r) => (
              <tr key={r._id} className="hover:bg-slate-50/70" data-testid={`po-profit-row-${r._id}`}>
                <td className="py-2 pr-2">
                  <p className="font-medium text-[#1a1a2e]">{r.po_number}</p>
                  <p className="text-[11px] text-slate-500 truncate max-w-[200px]">{r.client_name} · {r.category}</p>
                </td>
                <td className="py-2 px-2 text-right tabular-nums">{inr(r.subtotal)}</td>
                <td className="py-2 px-2 text-right tabular-nums text-slate-600">{inr(r.cost)}</td>
                <td className={`py-2 px-2 text-right tabular-nums font-semibold ${r.margin < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{inr(r.margin)}</td>
                <td className="py-2 px-2 text-right">
                  <span className={`inline-block px-1.5 py-0.5 rounded-md text-xs font-semibold tabular-nums ${r.margin_pct == null ? 'text-slate-400' : r.margin_pct < 0 ? 'bg-red-50 text-red-700' : r.margin_pct < 15 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {r.margin_pct == null ? '—' : `${r.margin_pct}%`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
